import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl} from 'react-intl';

import Link from '@stubhub/react-formatted-link';

import SEOJSONLDElement from '../react-seo-jsonld-element';

import {BreadcrumbsMessages} from './messages';

import './index.scss';

const homeText = (() => {
  return <FormattedMessage id="Breadcrumbs.home" defaultMessage="Home" description="Link to home page" />;
})();

const homeBC = {
  webURI: '/',
  name: homeText,
  id: 'home',
};

/**
 * BreadCrumbs
 * @param {*} breadcrumbs required <breadcrumbData>[] breadcrumbData: {cleanName, id, locale, name, seoURI, url, webURI}
 * @param {*} customerBreadcrumbDatas  {cleanName?, id, locale?, name, seoURI?, url?, webURI?}
 * @param {*} separator default '/'
 * @param {*} showHome default true
 * @param {*} useCleanName default false
 * @param {*} onClick required
 * @param {*} entityWebURI required
 * @param {*} intl
 */
const BreadCrumbs = ({
  breadcrumbs = [],
  customerBreadcrumbDatas = [],
  separator = '/',
  showHome = true,
  useCleanName = false,
  onClick,
  entityWebURI = '',
  intl,
  fqdn = '',
}) => {
  let bcs = breadcrumbs.slice(0); // Use a different array to avoid update original value
  if (customerBreadcrumbDatas.length) {
    // .push(breadcrumbData);
    bcs = [...bcs, ...customerBreadcrumbDatas];
  }

  /*
   * Use original array to be seo data source
   * cannot use breadcrumbs containing html tag.
   */
  const seoBcs = bcs.slice(0);

  if (showHome) {
    bcs.unshift(homeBC);
  }
  const lastIndex = bcs.length - 1;

  return (
    <section className="Breadcrumbs">
      {bcs.map((bc, index) => (
        <div className="Breadcrumbs__item" key={bc.id}>
          {/**
           * @TODO use FormattedLink instead of Link
           **/}
          {bc.webURI ? (
            <Link onClick={onClick} className="Breadcrumbs__link" href={`/${bc.webURI.replace(/^\//g, '')}`}>
              {useCleanName && bc.cleanName ? bc.cleanName : bc.name}
            </Link>
          ) : (
            <div className="Breadcrumbs__name">{useCleanName && bc.cleanName ? bc.cleanName : bc.name}</div>
          )}
          {index < lastIndex ? <span className="Breadcrumbs__sep">&nbsp;{separator}&nbsp;</span> : null}
        </div>
      ))}
      <div>
        <SEOJSONLDElement jsonld={getBreadCrumbsJSONLDMeta(seoBcs, intl, entityWebURI, showHome, useCleanName, fqdn)} />
      </div>
    </section>
  );
};

/**
 * Constants for schema markup terms
 */
const SCHEMA_ORG_URL = 'http://schema.org';
const SCHEMA_DEF_BREADCRUMB_LIST = 'BreadcrumbList';
const SCHEMA_DEF_BREADCRUMB_LISTITEM = 'ListItem';

const buildJSONLD = (bcs, entityWebURI, useCleanName, fqdn) =>
  bcs.map((bc, index) => ({
    '@type': SCHEMA_DEF_BREADCRUMB_LISTITEM,
    position: index + 1,
    item: {
      '@id': fqdn + (bcs.id === 'home' && index === 0 ? bc.webURI : `/${bc.webURI || entityWebURI}`), // Index === 0 ? '/' : '/' + (bc.webURI || entityWebURI),
      name: bc.seoSchemaName || (useCleanName && bc.cleanName) || bc.name,
    },
  }));

/**
 * Get breadcrumbs markup data in string format
 *
 * @param {array} bcs breadcrumbs seo data
 * @param {object} intl
 * @param {string} entityWebURI current entity web uri
 * @param {boolean} showHome=true de=true
 * @returns
 */
const getBreadCrumbsJSONLDMeta = (bcs, intl, entityWebURI, showHome = true, useCleanName = false, fqdn) => {
  // Add localized home crumb
  if (showHome) {
    bcs.unshift({
      webURI: '/',
      name: intl.formatMessage(BreadcrumbsMessages.home),
      id: 'home',
    });
  }

  const ldJSONMetaObj = {
    '@context': SCHEMA_ORG_URL,
    '@type': SCHEMA_DEF_BREADCRUMB_LIST,
    itemListElement: buildJSONLD(bcs, entityWebURI, useCleanName, fqdn),
  };

  return JSON.stringify(ldJSONMetaObj);
};

BreadCrumbs.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  separator: PropTypes.string,
  showHome: PropTypes.bool,
  onClick: PropTypes.func,
  entityWebURI: PropTypes.string.isRequired,
  useCleanName: PropTypes.bool,
  customerBreadcrumbDatas: PropTypes.array,
  intl: PropTypes.object,
  fqdn: PropTypes.string,
};

BreadCrumbs.defaultProps = {
  onClick: () => undefined,
};

export default injectIntl(BreadCrumbs);
